import { useEffect, useState } from "react";

/**
 * Delivery-pause ("blackout") status from the farm server.
 *
 * Michael declares periods he can't deliver from the farm-dashboard Delivery
 * tab. A pause never blocks an order: it just moves the delivery to the first
 * round after the break. This endpoint tells the storefront, per window, when
 * an order placed now would actually arrive and whether that's later than
 * normal ("deferred"). The cart and DeliveryCountdown use it to reassure the
 * customer their order still goes through, just a bit later.
 *
 * Fails open: if the request fails we return null and the UI behaves exactly
 * as it did before.
 *
 * Shape:
 *   {
 *     now: ISOString,
 *     anyDeferred: bool,
 *     message: string,                          // '' unless anyDeferred
 *     windows: { morning|evening|late: { delivery: ISOString|null, deferred: bool } },
 *     nextAvailable: { slotKey, date: ISOString } | null
 *   }
 */

const STATUS_URL = "https://teardrophydro.co.uk/garageFarm/delivery-blackouts/status";

export async function fetchDeliveryStatus() {
  try {
    const res = await fetch(STATUS_URL);
    if (!res.ok) return null;
    const data = await res.json();
    return data && typeof data === "object" && data.windows ? data : null;
  } catch {
    return null;
  }
}

export function useDeliveryStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await fetchDeliveryStatus();
      if (alive) {
        setStatus(data);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return { status, loading };
}

/** Short "Fri 5 Sep" style date label. */
export function formatResume(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

/** Per-window delivery info: { delivery, deferred } or null. */
export function slotDelivery(status, slot) {
  const w = status?.windows?.[slot];
  return w ? { delivery: w.delivery || null, deferred: !!w.deferred } : null;
}
